import { api, writeDB } from './store';
import { Address } from './company';
import { Event } from './event';
import { FilterTag, serializeQuery } from './filter';
import { handleError } from './notification';
import { history } from './history';
import { venuePath } from '../routes';
import { GetParams, PaginatedLoaderResponse, paginatedLoaderResponseFrom } from './data';
import { compact } from 'lodash';
import { SeatStatusType } from './contingent';
import { API, PaginatedResponse } from './api';
import { Point } from './seatmap/types';

export type PricingCategoryStyles = { [pricingCategoryId: string]: string };

export type VenueLayout = RestVenueLayout | GqlVenueLayout;

interface VenueLayoutCommon {
  id: string;
  name?: string;
  capacity?: number | null;
  seatsIoId?: string;
  scale?: number;
  pricingCategories?: Array<VenueLayoutPricingCategory>;
  detailPdfDownloadUrl?: string;
  readyForEvents?: boolean;
  archived?: boolean;
  requiresMapSelection?: boolean;
  cloned?: boolean;
}

type VenueLayoutPermissionsName = 'destroy' | 'update' | 'updateCapacity' | 'createPricingCategory';

export interface VenueLayoutPricingCategory {
  id: string;
  name?: string;
}

interface RestVenueLayout extends VenueLayoutCommon {
  $permissions: string[];
  importStatus?: string | null;
  venue?: {
    id: string;
  };
}

interface GqlVenueLayout extends VenueLayoutCommon {
  permissions: { [key in VenueLayoutPermissionsName]?: boolean };
  importStatus?: 'pending' | 'done' | 'error' | null;
  venue?: {
    id: string;
  };
}

export interface SeatStatusBase {
  id: string;
  seatId: string;
  blockReason?: string;
  permanent?: boolean;
}
export interface SeatStatus extends SeatStatusBase {
  status: SeatStatusType;
}
export interface SeatStatusResp extends SeatStatusBase {
  status: string;
}

export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface VenueSeat {
  x: number;
  y: number;
  id: string;
  row: string;
  name: string;
  orderId?: string;
  orderReference?: string;
  sectionName: string;
  pricingCategoryId: string;
}

export interface VenueSection {
  id: string;
  name: string;
  hullPoints: Point[];
  generalAdmission: boolean;
  pricingCategoryId?: string;
  seats: VenueSeat[];
}

export interface VenueSeatWithSection extends VenueSeat {
  section: VenueSection;
}

export interface AllocatedSeat extends VenueSeatWithSection {
  itemId?: string;
  status: SeatStatusType;
  blockReason?: string;
  permanent?: boolean;
  available: boolean;
}

export interface VenueGraphic {
  id: string;
  name: string;
  points: Array<[number, number]>;
  center: [number, number];
  imageUrl: string;
}

export interface VenuePlan {
  boundingBox: BoundingBox;
  sections: VenueSection[];
  graphics: Array<VenueGraphic>;
}

export interface VenuePlanWithPricingCategories extends VenuePlan {
  pricingCartegories: VenueLayoutPricingCategory[];
}

export interface Venue {
  id: string;
  slug: string;
  name: string;
  address: Address;
  $permissions: string[];
  venueLayouts: VenueLayout[];
  events: Event[];
}

export interface FlatVenuePlanSection {
  sections: {
    [key: string]: {
      id?: string;
      name?: string;
      capacity?: number;
    };
  };
  seats: {
    [key: string]: {
      id: string;
      row?: string;
      number?: string;
    };
  };
}

export interface GrouppedBlockedSection {
  name: string;
  allSeats: boolean;
  rows: Array<{
    name: string;
    seats: string[];
  }>;
}

/* VENUES CREATE */
export async function createVenue(venue: Venue): Promise<void> {
  try {
    const response = await api.createVenue(venue);
    writeDB.table('venues').upsert(response.data);
    history.push(venuePath(response.data as Venue));
  } catch (e) {
    handleError(e);
  }
}

/* VENUES READ */
export async function getRawVenue(venueId: string): Promise<PaginatedResponse<Venue>> {
  const response = await api.showVenue({ venueId });
  return response as PaginatedResponse<Venue>;
}

export async function getVenue(venueId: string): Promise<void> {
  api.indexVenues({});
  const response = await api.showVenue({ venueId });
  const venue = response.data as Venue;
  const response2 = await api.indexVenueEvents({ venueId });
  const eventsForVenue = response2.data as unknown as Event[];

  venue.events = eventsForVenue.map((efv) => ({
    ...efv,
    $permissions: efv.$permissions || [],
  }));
  writeDB.table('venues').upsert(venue);
}

export async function getRawVenues(
  { page, perPage, term }: GetParams,
  { singleGeneralAdmission }: { singleGeneralAdmission?: boolean } = {}
): Promise<PaginatedResponse<Venue[]>> {
  let params: Parameters<typeof api.indexVenues>[0] = {
    page: (page || 0) + 1,
    perPage,
    term,
  };
  if (singleGeneralAdmission) {
    params = { ...params, singleGeneralAdmission: true };
  }
  const response = await api.indexVenues(params);
  return response as PaginatedResponse<Venue[]>;
}

export async function getVenues({
  page,
  perPage,
  query = [],
  sortBy = 'name',
  sortDir = 'desc',
}: {
  page: number;
  perPage: number;
  query?: FilterTag[];
  sortBy?: string;
  sortDir?: string;
}): Promise<PaginatedLoaderResponse> {
  const serverVenues = await api.indexVenues({
    page: (page || 0) + 1,
    perPage,
    term: serializeQuery(query),
    sortBy,
    sortDir,
  });
  const completeData = await Promise.all(
    serverVenues.data.map(async (serverVenue) => {
      const result = await api.showVenue({
        venueId: serverVenue.id,
      });
      return result.data;
    })
  );
  writeDB.table('venues').upsert(completeData);
  return paginatedLoaderResponseFrom(serverVenues);
}

/* VENUES UPDATE */
export async function patchVenue(venue: Venue): Promise<void> {
  try {
    const response = await api.updateVenue({ venueId: venue.id, ...venue });
    writeDB.table('venues').upsert(response.data);
  } catch (e) {
    handleError(e);
  }
}

/* VENUES DELETE */
export async function deleteVenue(venueId: string): Promise<void> {
  try {
    await api.destroyVenue({
      venueId,
    });
    writeDB.table('venues').delete(venueId);
  } catch (e) {
    handleError(e);
  }
}

/* VENUELAYOUTS CREATE */
export async function createVenueLayout(
  venueId: string,
  { name, capacity, seatsIoId, scale }: VenueLayout
): Promise<VenueLayout | null> {
  try {
    const venue = writeDB.table('venues').find(venueId);
    const response = await api.createVenueLayout({
      venueId,
      name,
      capacity,
      seatsIoId,
      scale,
    });
    if (venue) {
      if (venue.venueLayouts) {
        venue.venueLayouts.push(response.data);
      } else {
        venue.venueLayouts = [response.data];
      }
      writeDB.table('venues').update(venueId, { venueLayouts: venue.venueLayouts });
      return response.data;
    }
  } catch (e) {
    handleError(e);
  }

  return null;
}

/* VENUELAYOUTS READ */
function mapSeatmapToVenuePlan(data: Awaited<ReturnType<API['showSeatmap']>>['data']): VenuePlan {
  const sections = data.sections?.map((section) => ({
    ...section,
    seats: section.seats?.map((seat) => {
      return {
        ...seat,
        name: seat.number,
      };
    }),
  }));
  return { ...data, sections } as unknown as VenuePlan;
}

export async function loadVenuePlan(event: Event): Promise<void> {
  const { data } = await api.showSeatmap({
    venueLayoutId: event.venueLayout.id,
  });
  const venuePlan = mapSeatmapToVenuePlan(data);
  writeDB.table('events').update(event, { venuePlan });
}

export async function loadVenuePlanFromVenueLayoutId(venueLayoutId: string): Promise<VenuePlan> {
  const { data } = await api.showSeatmap({ venueLayoutId, debug: true });
  return mapSeatmapToVenuePlan(data);
}

/* VENUELAYOUTS UPDATE */
export async function patchVenueLayout(venueId: string, venueLayout: VenueLayout): Promise<void> {
  try {
    const venue = writeDB.table('venues').find(venueId);
    const response = await api.updateVenueLayout({
      venueLayoutId: venueLayout.id,
      ...venueLayout,
    });
    if (venue) {
      const newVenueIndex = venue.venueLayouts.findIndex((vl) => {
        return vl.id === venueLayout.id;
      });
      if (venue.venueLayouts) {
        venue.venueLayouts[newVenueIndex] = response.data;
      } else {
        venue.venueLayouts = [response.data];
      }
      writeDB.table('venues').update(venueId, { venueLayouts: venue.venueLayouts });
    }
  } catch (e) {
    handleError(e);
  }
}

/* VENUELAYOUTS DELETE */
export async function deleteVenueLayout(venueLayoutId: string, venueId: string): Promise<void> {
  try {
    const venue = writeDB.table('venues').find(venueId);
    await api.destroyVenueLayout({
      venueLayoutId,
    });
    if (venue) {
      const newVenueLayouts = venue.venueLayouts.filter((vl) => vl.id !== venueLayoutId);
      writeDB.table('venues').update(venueId, { venueLayouts: newVenueLayouts });
    }
  } catch (e) {
    handleError(e);
  }
}

export function getVenueName(venue: Venue, options?: { address?: boolean }): string {
  return compact([
    venue.name,
    options?.address && compact([venue.address.address, venue.address.city]).join(', '),
  ]).join(' - ');
}
